import { unwrapXmlStyleContent } from './apiFieldText';
const FORM_FIELD_KEY = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;
const EMAIL_VALUE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Content length (chars) clamp for width weighting — short columns stay readable, long ones wrap. */
const MIN_WEIGHT_CH = 10;
const MAX_WEIGHT_CH = 36;
/** `SYSTEM_STATUS` is UPPER_SNAKE like form fields but is record state, not data to display. */
function isSystemStatusKey(key) {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '') === 'systemstatus';
}
function isFormFieldKey(key) {
    return FORM_FIELD_KEY.test(key) && !isSystemStatusKey(key);
}
export function formatGridHeader(field) {
    return field
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ');
}
function cellText(v) {
    if (typeof v === 'string')
        return unwrapXmlStyleContent(v).trim();
    if (typeof v === 'number' && Number.isFinite(v))
        return String(v);
    if (typeof v === 'boolean')
        return String(v);
    return '';
}
/** Form-field cells of each row as display text; rows with no values at all are dropped. */
export function toGridCellRows(rows) {
    const out = [];
    for (const row of rows) {
        const mapped = {};
        let hasValue = false;
        for (const [k, v] of Object.entries(row)) {
            if (!isFormFieldKey(k))
                continue;
            const t = cellText(v);
            mapped[k] = t;
            if (t)
                hasValue = true;
        }
        if (hasValue)
            out.push(mapped);
    }
    return out;
}
/** Columns in API field order; columns that are empty in every row are omitted. */
export function deriveGridColumns(rows) {
    const order = [];
    const seen = new Set();
    for (const row of rows) {
        for (const k of Object.keys(row)) {
            if (!seen.has(k)) {
                seen.add(k);
                order.push(k);
            }
        }
    }
    const measured = [];
    for (const field of order) {
        const values = rows.map((r) => r[field] ?? '').filter(Boolean);
        if (values.length === 0)
            continue;
        const header = formatGridHeader(field);
        const longest = Math.max(header.length, ...values.map((v) => v.length));
        measured.push({
            field,
            header,
            isEmail: values.every((v) => EMAIL_VALUE.test(v)),
            weight: Math.min(MAX_WEIGHT_CH, Math.max(MIN_WEIGHT_CH, longest)),
        });
    }
    const totalWeight = measured.reduce((sum, c) => sum + c.weight, 0) || 1;
    return measured.map(({ weight, ...c }) => ({ ...c, widthPercent: (weight / totalWeight) * 100 }));
}
